import {
  clean,
  first,
  getAbsoluteUrl,
  round2,
  slugify,
  titleFromSlug,
} from './format';

// A list identity is `{ item_list_id, item_list_name }`. It is pushed once at
// event level and repeated on every item, exactly as the spec sample does.
//
// `item_list_id` is a stable, language-independent slug; `item_list_name` is the
// English label an analyst reads in GA4.
export const buildList = (id, name) => {
  const item_list_id = clean(id);
  if (!item_list_id) return null;

  const item_list_name = clean(name);
  return { item_list_id, ...(item_list_name && { item_list_name }) };
};

// Spec §5.3.6: category lists use the `c_<slug>` convention. The slug is the
// category's `innova_url`, which is identical in AR and EN.
//
// `name` must be an English label. Callers usually have only the locale's
// category title, which would give the same list two different names, so the
// name is derived from the slug instead unless an English one is passed.
export const categoryList = (slug, name) => {
  const key = slugify(slug);
  return key ? buildList(`c_${key}`, clean(name) || titleFromSlug(key)) : null;
};

// Homepage and category-landing carousels. Sections are ordered, repeatable and
// CMS-driven, so the English title is what identifies one; `key`+`position` is
// the fallback for a section that ships without one.
export const homeSectionList = (section) => {
  const englishTitle = clean(section?.['title-en']);
  const slug = slugify(englishTitle);
  const fallback = [clean(section?.key), clean(section?.position)]
    .filter(Boolean)
    .join('_');

  return buildList(
    slug ? `hp_${slug}` : fallback && `hp_${fallback}`,
    englishTitle || titleFromSlug(section?.key)
  );
};

// Lists that are the same wherever they appear.
export const PRODUCT_LISTS = {
  searchResults: buildList('search_results', 'Search Results'),
  searchSuggestions: buildList('search_suggestions', 'Search Suggestions'),
  wishlist: buildList('wishlist', 'Favourites'),
  pdpRelated: buildList('pdp_related', 'PDP Related Products'),
  pdpUpSell: buildList('pdp_up_sell', 'PDP Up-Sell Products'),
  pdpAlsoViewed: buildList('pdp_also_viewed', 'PDP Also Viewed'),
  pdpCombo: buildList('pdp_combo', 'PDP Combo Offer'),
  pdpBundle: buildList('pdp_bundle', 'PDP Frequently Bought Together'),
};

const CATEGORY_KEYS = [
  'item_category',
  'item_category2',
  'item_category3',
  'item_category4',
  'item_category5',
];

// Algolia records carry no `_category_`. They hold the path under
// `categories.levelN` as `Root /// Child /// Grandchild`, one entry per tree the
// product belongs to — a product sits under its merchandising tree and again
// under `Brands`. The deepest path wins, and an equally deep tie goes to the
// root listed first, which is the merchandising one.
const ALGOLIA_SEPARATOR = '///';

const getAlgoliaCategories = (product) => {
  const levels = product?.categories;
  if (!levels) return [];

  const roots = [].concat(levels?.level0 || []).map(String);
  const paths = Object.keys(levels)
    .filter((key) => /^level\d+$/.test(key))
    .flatMap((key) => [].concat(levels[key] || []).map(String));

  const rank = (path) => ({
    depth: path.split(ALGOLIA_SEPARATOR).length,
    root: roots.findIndex((root) => path === root || path.startsWith(root)),
  });

  const best = paths.reduce((winner, path) => {
    if (!winner) return path;
    const a = rank(path);
    const b = rank(winner);
    if (a.depth !== b.depth) return a.depth > b.depth ? path : winner;
    if (a.root < 0) return winner;
    return b.root < 0 || a.root < b.root ? path : winner;
  }, '');

  return best.split(ALGOLIA_SEPARATOR).map(clean).filter(Boolean);
};
const getCategoryPath = (categories) => {
  const [root, ...rest] = categories.filter(Boolean);
  if (!root) return [];

  const level = (category) => Number(category?.level);
  if (!Number.isFinite(level(root))) return [root, ...rest];

  const path = [root];
  for (const category of rest) {
    if (level(category) !== level(path.at(-1)) + 1) break;
    path.push(category);
  }
  return path;
};

// The product's own category path, deepest last. Three shapes exist depending on
// the endpoint, so fall back from one to the next.
const getItemCategories = (product) => {
  const fromTree = [
    product?._category_?.main_category,
    ...(product?._category_?.sub_categories || []),
  ];
  const fromCartLine = !fromTree.some(Boolean);
  const source = fromTree.some(Boolean)
    ? fromTree
    : product?.extension_attributes?.category_names || [];

  const fromMagento = getCategoryPath(source)
    .map((category) => clean(category?.name))
    .filter(Boolean)
    .slice(0, fromCartLine ? 2 : CATEGORY_KEYS.length);

  const names = (
    fromMagento.length ? fromMagento : getAlgoliaCategories(product)
  ).slice(0, CATEGORY_KEYS.length);

  return Object.fromEntries(names.map((name, i) => [CATEGORY_KEYS[i], name]));
};

// Catalog products price through `prices_with_tax`; a cart line prices through
// `price_incl_tax` and `extension_attributes.original_price`. The cart-line
// fields are fallbacks only, so the list events are unaffected.
const getUnitPrice = (product) =>
  product?.prices_with_tax?.price ??
  product?.prices_with_tax?.final_price ??
  product?.price_incl_tax;

const getShelfPrice = (product) =>
  product?.prices_with_tax?.original_price ??
  product?.extension_attributes?.original_price ??
  product?.original_price;

const getItemBrand = (product) =>
  clean(first(product?.option_text_brands)) ||
  clean(product?.extension_attributes?.brand_name) ||
  clean(product?.brand_label);

const getCoupon = (product) => {
  const rule = (product?.cart_rule || []).find(
    (item) => clean(item?.labels?.at(2)) || clean(item?.name)
  );

  return (
    clean(rule?.labels?.at(2)) ||
    clean(rule?.name) ||
    clean(first(product?.option_text_offer_type)) ||
    clean(first(product?.extension_attributes?.option_text_offer_type))
  );
};

const isEnglish = (value) => !!value && !/[؀-ۿ]/.test(value);

// Mirrors the card's own in-stock test, so the event never disagrees with what
// the shopper is looking at.
const getAvailability = (product, item_id, unavailableSkus) => {
  if (item_id && unavailableSkus?.includes(item_id)) return 'Out of Stock';

  return !product?.stock ||
    (product?.stock?.is_in_stock && product?.stock?.qty > 0)
    ? 'In Stock'
    : 'Out of Stock';
};

export const getUnavailableSkus = (unavailableItems) => {
  const fromCheck = (unavailableItems || [])
    .map((item) => clean(first(item?.sku)))
    .filter(Boolean);

  const fromQuery =
    typeof window === 'object'
      ? (new URLSearchParams(window.location.search).get('items') || '')
          .split(',')
          .map(clean)
          .filter(Boolean)
      : [];

  return [...new Set([...fromCheck, ...fromQuery])];
};

const getImageLink = (product) => {
  const productImage = product?.extension_attributes?.product_image;
  return (
    clean(product?._media_?.image?.at(0)?.image) ||
    clean(product?.image_url) ||
    (productImage &&
      clean(
        `${process.env.NEXT_PUBLIC_IMG_BASEURL}/media/catalog/product/${productImage}`
      ))
  );
};

// The spec's `item_id` is the SKU. Exported because attribution is keyed on it
// and has to be read *before* the item is built.
export const getItemId = (product) =>
  clean(first(product?.extension_attributes?.product_sku)) ||
  clean(first(product?.sku)) ||
  clean(product?.objectID);

/**
 * One entry of the spec's `items` array for a product seen in a list.
 * Returns `null` when the product has no id — spec §2 forbids empty values, and
 * an item without `item_id` is not usable in GA4.
 */
export function buildListItem({
  product,
  index,
  list,
  locale,
  quantity,
  unavailableSkus,
}) {
  const item_id = getItemId(product);
  if (!item_id) return null;

  const item_name = clean(first(product?.name));
  const price = round2(getUnitPrice(product) ?? getShelfPrice(product));
  const shelf_price = round2(getShelfPrice(product) ?? getUnitPrice(product));
  const discount = round2(Math.max((shelf_price ?? 0) - (price ?? 0), 0));

  // Only the cart events carry a quantity; the list events have none.
  const units = Number(quantity);

  const item_brand = getItemBrand(product);

  const rawCoupon = getCoupon(product);
  const coupon =
    isEnglish(rawCoupon) || !discount
      ? rawCoupon
      : `Save ${Math.round((discount / (shelf_price || 1)) * 100)}%`;
  const item_link = getAbsoluteUrl(
    product?.innova_url ?? product?.extension_attributes?.innova_url,
    locale
  );
  const item_image_link = getImageLink(product);

  return {
    item_id,
    ...(item_name && { item_name }),
    ...(shelf_price !== undefined && { shelf_price }),
    ...(price !== undefined && { price }),
    ...(coupon && { coupon }),
    ...(discount !== undefined && { discount }),
    index: Number(index) || 0,
    ...(item_brand && { item_brand }),
    ...getItemCategories(product),
    ...list,
    item_availability: getAvailability(product, item_id, unavailableSkus),
    ...(item_link && { item_link }),
    ...(item_image_link && { item_image_link }),
    // Innova's Rx vs OTC dimension — the "Prescription Needed" badge.
    prescription_required:
      product?.prescription_upload_required &&
      product?.prescription_upload_required !== 'No'
        ? 'Yes'
        : 'No',
    ...(Number.isFinite(units) && units > 0 && { quantity: units }),
  };
}
